<template>
    <div class="request-form">
        <!-- customer and department -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label><strong>Customer / Client</strong></label>
                <client-ac
                    v-model="model.ContactId"
                    placeholder="Select a client (optional)"
                    @update:modelValue="onClientChange"
                ></client-ac>
            </div>
            <div class="col-md-3 mb-3">
                <label><strong>Department / Category</strong></label>
                <div class="input-group">
                    <select 
                        v-if="departmentOptions.length > 0"
                        class="form-select form-select-sm"
                        v-model="model.DepartmentCategory"
                    >
                        <option :value="undefined" disabled selected>Select Department...</option>
                        <option 
                            v-for="dept in departmentOptions" 
                            :key="dept.value" 
                            :value="dept.value"
                        >
                            {{ dept.label }}
                        </option>
                    </select>
                    <select 
                        v-else
                        class="form-select form-select-sm"
                        disabled
                    >
                        <option>Use button to add department</option>
                    </select>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="openDepartmentSelector"
                        title="Manage departments"
                    >
                        <Search :size="16" />
                    </button>
                </div>
            </div>
            <div class="col-md-3 mb-3">
                <label><strong>Request Method</strong></label>
                <div class="input-group">
                    <select 
                        v-if="requestMethodOptions.length > 0"
                        class="form-select form-select-sm"
                        v-model="model.RequestMethod"
                    >
                        <option :value="undefined" disabled selected>Select Method...</option>
                        <option 
                            v-for="method in requestMethodOptions" 
                            :key="method.value" 
                            :value="method.value"
                        >
                            {{ method.label }}
                        </option>
                    </select>
                    <select 
                        v-else
                        class="form-select form-select-sm"
                        disabled
                    >
                        <option>Use button to add method</option>
                    </select>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="openRequestMethodSelector"
                        title="Manage request methods"
                    >
                        <Search :size="16" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Link to Existing Work Order + Technician -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <div v-if="!jobSelectorVisible" class="-text-center">
                        <button 
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="toggleJobSelector"
                        >
                        <Link :size="16" class="me-1" />
                        Link related Work Order
                        </button>
                </div>
                <div v-else>
                    <label><strong>Link to Work Order</strong></label>
                    <div class="d-flex align-items-center gap-2">
                        <div class="flex-grow-1">
                            <job-ac
                                v-model="jobIdString"
                                :client="model.ContactId?.toString() || ''"
                                placeholder="Search for a work order..."
                                @change="onJobChange"
                            ></job-ac>
                        </div>
                        <button 
                            v-if="!model.RequestId"
                            type="button" 
                            class="btn btn-sm btn-outline-secondary"
                            @click="toggleJobSelector"
                            title="Hide work order selector"
                        >
                            <span>&times;</span>
                        </button>
                    </div>
                    <small class="text-muted">Link this request to an existing work order</small>
                </div>
            </div>
            <div class="col-md-6 mb-3">
                <label><strong>Technician (Owner)</strong></label>
                <tech-ac
                    v-model="model.Owner"
                    placeholder="Select a technician..."
                ></tech-ac>
            </div>
        </div>

        <!-- Row 2: Client and Requestor -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <!-- customer -->
                <div>
                    <label class="d-flex justify-content-between align-items-center">
                        <span class="fw-normal">Request Title</span>
                        <small><em>({{ (model.RequestTitle || '').length }}/255)</em></small>
                    </label>
                    <input
                        type="text"
                        v-model="model.RequestTitle"
                        class="form-control form-control-sm"
                        placeholder="Brief title for this request"
                        maxlength="255"
                        required
                    />
                </div>
            </div>
            <div class="col-md-6 mb-3">
                <label><strong>Requestor</strong></label>
                <div class="input-group">
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="model.Requestor"
                        placeholder="Name of person making request"
                    />
                    <button 
                        v-if="model.ContactId"
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="openStaffLookup"
                        title="Lookup staff member"
                    >
                        <Search :size="16" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Row 3: Priority -->
        <div class="row">
            <div class="col-md-12 mb-3">
                <label><strong>Priority</strong></label>
                <div class="btn-group w-100" role="group" aria-label="Priority selection">
                    <input type="radio" class="btn-check" name="priority" id="priority-none" value="None" v-model="model.Priority" autocomplete="off">
                    <label class="btn btn-outline-secondary" for="priority-none">
                        <Circle :size="16" class="me-1" />
                        None
                    </label>

                    <input type="radio" class="btn-check" name="priority" id="priority-low" value="Low" v-model="model.Priority" autocomplete="off">
                    <label class="btn btn-outline-info" for="priority-low">
                        <Info :size="16" class="me-1" />
                        Low
                    </label>

                    <input type="radio" class="btn-check" name="priority" id="priority-medium" value="Medium" v-model="model.Priority" autocomplete="off">
                    <label class="btn btn-outline-warning" for="priority-medium">
                        <AlertTriangle :size="16" class="me-1" />
                        Medium
                    </label>

                    <input type="radio" class="btn-check" name="priority" id="priority-high" value="High" v-model="model.Priority" autocomplete="off">
                    <label class="btn btn-outline-orange" for="priority-high">
                        <AlertTriangle :size="16" class="me-1" />
                        High
                    </label>

                    <input type="radio" class="btn-check" name="priority" id="priority-critical" value="Critical" v-model="model.Priority" autocomplete="off">
                    <label class="btn btn-outline-danger" for="priority-critical">
                        <AlertCircle :size="16" class="me-1" />
                        Critical
                    </label>
                </div>
            </div>
        </div>

        <!-- Row 4: Location, Asset -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <kt-input
                    type="text"
                    label="Location"
                    v-model="model.Location"
                    input-class="form-control form-control-sm"
                    placeholder="Location"
                ></kt-input>
            </div>
            <div class="col-md-6 mb-3">
                <kt-input
                    type="text"
                    label="Asset"
                    v-model="model.Asset"
                    input-class="form-control form-control-sm"
                    placeholder="Asset name or ID"
                ></kt-input>
            </div>
        </div>

        <!-- Row 4: Affected User, Needed By -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <kt-input
                    type="text"
                    label="Affected User"
                    v-model="model.AffectedUser"
                    input-class="form-control form-control-sm"
                    placeholder="User affected by this issue"
                ></kt-input>
            </div>
            <div class="col-md-6 mb-3">
                <kt-input
                    type="date"
                    label="Needed By"
                    v-model="model.DateDue"
                    input-class="form-control form-control-sm"
                ></kt-input>
            </div>
        </div>

        <!-- Row 5: Email, Primary Phone -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <kt-input
                    type="email"
                    label="Email"
                    v-model="model.Email"
                    input-class="form-control form-control-sm"
                    placeholder="Contact email"
                ></kt-input>
            </div>
            <div class="col-md-6 mb-3">
                <kt-input
                    type="text"
                    label="Primary Phone"
                    v-model="model.Phone"
                    input-class="form-control form-control-sm"
                    placeholder="Primary contact phone"
                ></kt-input>
            </div>
        </div>

        <div class="row">

            <!-- Additional Emails -->
            <div class="col-md-6 mb-3">
                <label><strong>Additional Emails</strong></label>
                <div class="emails-container">
                    <div 
                        v-for="(emailEntry, index) in additionalEmails" 
                        :key="index"
                        class="d-flex align-items-center gap-2 mb-2"
                    >
                        <input 
                            type="email" 
                            class="form-control form-control-sm flex-grow-1"
                            v-model="emailEntry.email"
                            placeholder="Email address"
                        />
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger"
                            @click="removeEmail(index)"
                            title="Remove email"
                        >
                            <Trash2 :size="14" />
                        </button>
                    </div>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="addEmail"
                    >
                        <Plus :size="14" class="me-1" />
                        Add Email
                    </button>
                </div>
            </div>

            <!-- Row 5b: Additional Phone Numbers -->
            <div class="col-md-6 mb-3">
                <label><strong>Additional Phone Numbers</strong></label>
                <div class="phone-numbers-container">
                    <div 
                        v-for="(phone, index) in phoneNumbers" 
                        :key="index"
                        class="d-flex align-items-center gap-2 mb-2"
                    >
                        <input 
                            type="text" 
                            class="form-control form-control-sm flex-grow-1"
                            v-model="phone.number"
                            placeholder="Phone number"
                        />
                        <select 
                            class="form-select form-select-sm" 
                            style="width: 120px;"
                            v-model="phone.type"
                        >
                            <option value="mobile">Mobile</option>
                            <option value="landline">Landline</option>
                        </select>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger"
                            @click="removePhoneNumber(index)"
                            title="Remove phone number"
                        >
                            <Trash2 :size="14" />
                        </button>
                    </div>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="addPhoneNumber"
                    >
                        <Plus :size="14" class="me-1" />
                        Add Phone Number
                    </button>
                </div>
            </div>
            
        </div>

        <!-- Row 6: Description -->
        <div class="row">
            <div class="col-md-12 mb-3">
                <label><strong>Description</strong></label>
                <textarea 
                    class="form-control form-control-sm" 
                    v-model="model.Description"
                    rows="4"
                    placeholder="Describe the request in detail..."
                ></textarea>
            </div>
        </div>

        <!-- File Attachments Section -->
        <div class="row" v-if="model.Id">
            <div class="col-md-12 mb-3">
                <label><strong><Paperclip :size="16" class="me-1" />Attachments</strong></label>
                <kt-upload 
                    @process="processFiles($event)"
                    multiple="1"    
                ></kt-upload>

                <table class="table table-sm table-striped mt-3" v-if="files.length">
                    <thead>
                        <tr>
                            <th style="width: 80px">Actions</th>
                            <th>File Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="file in files" :key="file.Id">
                            <td>
                                <button class="btn btn-sm btn-outline-danger" @click="showDeleteFile(file)">
                                    <Trash2 :size="14" />
                                </button>
                            </td>
                            <td>
                                <a :href="AttachmentService.getDownloadUrl(file.Id)" target="_blank">
                                    {{ file.Title }}
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="!files.length" class="alert alert-secondary mt-2">
                    <i class="fa fa-file"></i> No files uploaded yet.
                </div>
            </div>
        </div>

        <!-- Notify Customer on Updates -->
        <div class="row">
            <div class="col-md-12 mb-3">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="notifyUpdates"
                        v-model="model.NotifyUpdates"
                    />
                    <label class="form-check-label" for="notifyUpdates">
                        <strong>Notify Customer on Updates</strong>
                        <small class="text-muted ms-2">Send notifications when this request is updated</small>
                    </label>
                </div>
            </div>
        </div>

        <!-- Staff Lookup Modal -->
        <staff-lookup-modal
            v-if="showStaffLookup"
            :visible="showStaffLookup"
            :client-id="model.ContactId"
            @close="showStaffLookup = false"
            @select="onStaffSelected"
        />

        <!-- Department Selector Modal -->
        <department-selector-modal
            v-if="showDepartmentSelector"
            :visible="showDepartmentSelector"
            :current-value="model.DepartmentCategory"
            :can-manage="isAdmin"
            @close="showDepartmentSelector = false"
            @select="onDepartmentSelected"
            @departments-changed="loadDepartments"
        />

        <!-- Request Method Selector Modal -->
        <request-method-selector-modal
            v-if="showRequestMethodSelector"
            :visible="showRequestMethodSelector"
            :current-value="model.RequestMethod"
            :can-manage="isAdmin"
            @close="showRequestMethodSelector = false"
            @select="onRequestMethodSelected"
            @methods-changed="loadRequestMethods"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { WorkOrderModel } from '@/models/WorkOrderModel';
import KtInput from '@/components/UI/KtInput.vue';
import KtSelect from '@/components/UI/KtSelect.vue';
import ClientAc from '@/components/Clients/ClientAc.vue';
import JobAc from '@/components/Jobs/JobAc.vue';
import TechAc from '@/components/Techs/TechAc.vue';
import StaffLookupModal from '@/components/Requests/StaffLookupModal.vue';
import DepartmentSelectorModal from '@/components/Requests/DepartmentSelectorModal.vue';
import RequestMethodSelectorModal from '@/components/Requests/RequestMethodSelectorModal.vue';
import OptionsService from '@/services/OptionsService';
import { Search, Circle, Info, AlertTriangle, AlertCircle, Link, Trash2, Plus, Paperclip } from 'lucide-vue-next';
import type { PhoneEntry, EmailEntry } from '@/models/WorkOrderModel';
import { usePermissions } from '@/composables/usePermissions';
import KtUpload from '@/components/UI/KtUpload.vue';
import AttachmentService from '@/services/AttachmentService';
import Api from '@/services/Api';
import Alert from '@/services/Alert';

const { isAdmin } = usePermissions();

interface Department {
    name: string;
    email: string;
}

const model = defineModel<WorkOrderModel>({
    default: () => new WorkOrderModel()
});

const showStaffLookup = ref(false);
const showDepartmentSelector = ref(false);
const showRequestMethodSelector = ref(false);
const departmentOptions = ref<{ label: string; value: string }[]>([]);
const requestMethodOptions = ref<{ label: string; value: string }[]>([]);
const showJobSelector = ref(false);
const files = ref<any[]>([]);

// File attachment functions
const loadFiles = async () => {
    if (model.value.Id) {
        files.value = await Api.loadRequestFiles(model.value.Id);
    }
};

const processFiles = async (uploadedFiles: File[]) => {
    for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const formData = new FormData();
        formData.append("File", file);
        formData.append("Type", "request");
        formData.append("RequestId", model.value.Id?.toString() || '');
        await Api.saveFile(formData);
    }
    await loadFiles();
};

const showDeleteFile = async (file: any) => {
    Alert.confirm("Are you sure you want to remove that file?", async () => {
        await Api.deleteFile(file.Id);
        await loadFiles();
    });
};

// Phone numbers management - synced with model.AdditionalData.phoneNumbers
const phoneNumbers = computed({
    get: (): PhoneEntry[] => {
        return model.value.AdditionalData?.phoneNumbers || [];
    },
    set: (val: PhoneEntry[]) => {
        if (!model.value.AdditionalData) {
            model.value.AdditionalData = {};
        }
        model.value.AdditionalData.phoneNumbers = val;
    }
});

const addPhoneNumber = () => {
    const newPhone: PhoneEntry = { number: '', type: 'mobile' };
    if (!model.value.AdditionalData) {
        model.value.AdditionalData = { phoneNumbers: [] };
    }
    if (!model.value.AdditionalData.phoneNumbers) {
        model.value.AdditionalData.phoneNumbers = [];
    }
    model.value.AdditionalData.phoneNumbers.push(newPhone);
};

const removePhoneNumber = (index: number) => {
    if (model.value.AdditionalData?.phoneNumbers) {
        model.value.AdditionalData.phoneNumbers.splice(index, 1);
    }
};

// Email management - synced with model.AdditionalData.emails
const additionalEmails = computed({
    get: (): EmailEntry[] => {
        return model.value.AdditionalData?.emails || [];
    },
    set: (val: EmailEntry[]) => {
        if (!model.value.AdditionalData) {
            model.value.AdditionalData = {};
        }
        model.value.AdditionalData.emails = val;
    }
});

const addEmail = () => {
    const newEmail: EmailEntry = { email: '' };
    if (!model.value.AdditionalData) {
        model.value.AdditionalData = { emails: [] };
    }
    if (!model.value.AdditionalData.emails) {
        model.value.AdditionalData.emails = [];
    }
    model.value.AdditionalData.emails.push(newEmail);
};

const removeEmail = (index: number) => {
    if (model.value.AdditionalData?.emails) {
        model.value.AdditionalData.emails.splice(index, 1);
    }
};

// Computed to convert RequestId to string for JobAc component
const jobIdString = computed({
    get: () => model.value.RequestId?.toString() || '',
    set: (val: string) => {
        if (val && val !== '' && !isNaN(Number(val))) {
            model.value.RequestId = Number(val);
        }
    }
});

// Computed to determine if job selector should be visible
const jobSelectorVisible = computed(() => {
    return showJobSelector.value || !!model.value.RequestId;
});

const toggleJobSelector = () => {
    showJobSelector.value = !showJobSelector.value;
};

const onJobChange = (jobId: string | number | null) => {
    // Only update if we have a valid job ID (not empty string, null, or undefined)
    // Note: v-model handles the update, this is a backup
    if (jobId && jobId !== '' && !isNaN(Number(jobId))) {
        model.value.RequestId = Number(jobId);
    }
};

const DEPARTMENT_OPTION_KEY = 'departments_json';
const REQUEST_METHOD_OPTION_KEY = 'request_methods_json';

interface RequestMethod {
    name: string;
}

const loadDepartments = async () => {
    try {
        const optionValue = await OptionsService.get(DEPARTMENT_OPTION_KEY);
        if (optionValue) {
            const departments: Department[] = JSON.parse(optionValue);
            departmentOptions.value = departments.map(d => ({
                label: d.name,
                value: d.name
            }));
        } else {
            // No departments configured - show empty state
            departmentOptions.value = [];
        }
    } catch (error) {
        console.error('Error loading departments:', error);
        departmentOptions.value = [];
    }
};

const loadRequestMethods = async () => {
    try {
        const optionValue = await OptionsService.get(REQUEST_METHOD_OPTION_KEY);
        if (optionValue) {
            const methods: RequestMethod[] = JSON.parse(optionValue);
            requestMethodOptions.value = methods.map(m => ({
                label: m.name,
                value: m.name
            }));
        } else {
            requestMethodOptions.value = [];
        }
    } catch (error) {
        console.error('Error loading request methods:', error);
        requestMethodOptions.value = [];
    }
};

const openDepartmentSelector = () => {
    showDepartmentSelector.value = true;
};

const openRequestMethodSelector = () => {
    showRequestMethodSelector.value = true;
};

const onDepartmentSelected = (dept: Department) => {
    model.value.DepartmentCategory = dept.name;
    showDepartmentSelector.value = false;
};

const onRequestMethodSelected = (method: RequestMethod) => {
    model.value.RequestMethod = method.name;
    showRequestMethodSelector.value = false;
};

const onClientChange = (clientId: number | null) => {
    // Clear requestor info when client changes
    if (!clientId) {
        model.value.Requestor = '';
        model.value.Email = '';
        model.value.Phone = '';
    }
};

const openStaffLookup = () => {
    if (model.value.ContactId) {
        showStaffLookup.value = true;
    }
};

const onStaffSelected = (staff: any) => {
    if (staff) {
        model.value.Requestor = staff.Name || `${staff.FirstName || ''} ${staff.LastName || ''}`.trim();
        model.value.Email = staff.Email || '';
        model.value.Phone = staff.Phone || staff.Mobile || '';
    }
    showStaffLookup.value = false;
};

onMounted(() => {
    loadDepartments();
    loadRequestMethods();
    loadFiles();
});
</script>

<style scoped>
.request-form {
    min-height: 200px;
}
</style>
